import { Box, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { ContactAttributes } from '../../../@types/connect.type';
import { useConnect } from '../ConnectProvider';

// Contact attributes attached by the IVR / CRM lookup. Surfaced so agents
// can see the context the platform already knows before asking the
// customer to repeat themselves.
//
// Attribute keys often follow a `<group>-<key>` convention from contact
// flows (e.g. `c-name`, `c-account`, `n-disposition`). We split on the
// first `-` to render groups; keys without a prefix fall under "Other".

interface AttributeGroup {
  label: string;
  entries: Array<{ key: string; value: string }>;
}

const PREFIX_LABELS: Record<string, string> = {
  c: 'Customer',
  n: 'Notes / wrap-up',
  a: 'Agent',
  q: 'Queue',
  s: 'System',
};

const groupAttributes = (attrs: ContactAttributes): AttributeGroup[] => {
  const buckets = new Map<string, AttributeGroup>();
  for (const key of Object.keys(attrs).sort()) {
    const dashIndex = key.indexOf('-');
    const prefix = dashIndex > 0 ? key.slice(0, dashIndex) : '';
    const display = dashIndex > 0 ? key.slice(dashIndex + 1) : key;
    const label = PREFIX_LABELS[prefix] ?? (prefix ? prefix.toUpperCase() : 'Other');
    const bucket = buckets.get(label) ?? { label, entries: [] };
    bucket.entries.push({ key: display || key, value: attrs[key] });
    buckets.set(label, bucket);
  }
  // Render Customer first if present, then everything else alphabetised.
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.label === 'Customer') return -1;
    if (b.label === 'Customer') return 1;
    return a.label.localeCompare(b.label);
  });
};

export const AttributesPanel: React.FC = () => {
  const { attributes, contact } = useConnect();
  const groups = useMemo(() => groupAttributes(attributes), [attributes]);

  if (!contact) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No active call. Attributes from the IVR appear here once a call connects.
        </Typography>
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          The IVR didn&apos;t attach any attributes to this call.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack sx={{ height: '100%', overflowY: 'auto' }}>
      {groups.map((group) => (
        <Box key={group.label}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 2,
              py: 1,
              bgcolor: 'v4.tableEven',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'v4.border',
            }}
          >
            {group.label}
          </Typography>
          <List dense disablePadding>
            {group.entries.map(({ key, value }) => (
              <ListItem key={key} divider>
                <ListItemText
                  primary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {key.replace(/[_-]/g, ' ')}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {value}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Stack>
  );
};
